import { TransferProductResponse, TransfersProduct } from "../model";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

interface SaveTransferResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    productId?: number;
}

const parseResponse = async (response: Response | undefined) => {
    if (!response) {
        return { message: undefined, productId: undefined };
    }

    try {
        const data = await response.clone().json();
        if (typeof data === "string") {
            const numeric = Number(data);
            return {
                message: data,
                productId: Number.isNaN(numeric) ? undefined : numeric
            };
        }

        if (data && typeof data === "object") {
            const message = typeof data.message === "string" ? data.message : undefined;
            const productId = typeof data.productId === "number"
                ? data.productId
                : typeof data.id === "number"
                    ? data.id
                    : undefined;
            return { message, productId };
        }
    } catch {
        // fall through to text parsing
    }

    try {
        const text = await response.text();
        const numeric = Number(text);
        return {
            message: text,
            productId: Number.isNaN(numeric) ? undefined : numeric
        };
    } catch {
        return { message: undefined, productId: undefined };
    }
};

export async function saveTransferProduct(product: TransfersProduct, divisionId: number): Promise<SaveTransferResult> {
    const localApi = await getLocalApi();

    const payload: TransferProductResponse = {
        productId: Number(product?.id ?? 0),
        productName: product.name,
        portId: Number(product.port),
        transferTypeId: Number(product.transferType),
        productCategoryId: Number(product.productCategoryId ?? 0),
        accountCategoryId: Number(product.accountCategoryId ?? 0),
        active: product.active ?? true,
        divisionId,
    };

    const response = await localApi.call(
        apiConfig.transfers_save.replace("{divisionId}", divisionId.toString()),
        "PUT",
        JSON.stringify(payload)
    );

    if (!response) {
        return { ok: false, message: "No response from API", errors: ["No response from API"] };
    }

    const { message, productId } = await parseResponse(response);
    
    let rawText = "";
    try {
        rawText = await response.clone().text();
    } catch {
        rawText = "";
    }

    let parsed: any = null;
    if (rawText) {
        try {
            parsed = JSON.parse(rawText);
        } catch {
            parsed = rawText;
        }
    }

    const errors = ApiErrors.extractAllErrors(parsed, response, "Failed to save transfer");
    const finalMessage = response.ok
        ? (message || "Transfer product saved")
        : ApiErrors.formatErrors(errors);

    return { ok: response.ok, message: finalMessage, errors: response.ok ? undefined : errors, productId };
}

